import { LoadingOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react'
import React, { useRef, useState, useEffect, useCallback } from 'react'
import { BaseModal } from '../../../../common/components/BaseModal/BaseModal'
import { PrimaryButton } from '../../../../common/components/buttons/NewButtons.styles'
import { displayMoneyValue } from '../../../../common/utils/number-display.utils'
import { useStore } from '../../../app/data/root.store'
import { DeepAnalysisResultsRow } from './DeepAnalysisResultsRow'
import { VaultFileIcon, VaultChevronDownIcon } from '../../assets/icons'
import { DeepAnalysisReviewResultContent, DeepAnalysisReviewResultFooter, DeepAnalysisReviewResultHeader, DeepAnalysisReviewResultHeaderTitle, DeepAnalysisReviewResultsWrapper, DropdownIconWrapper, FooterTotalItemsLabel, FooterTotalItemsValue, FooterTotalsItem, FooterTotalsWrapper, ReviewHeaderDropdownIconWrapper, ReviewHeaderDropdownWrapper, ReviewHeaderMessageText, ReviewHeaderMessageWrapper, ReviewHeaderWrapper, VaultSingleFileDescriptionEmpty } from './DeepAnalysisReviewResultModal.styles'
import { ReactComponent as ModalCloseIcon } from './assets/modal-close-icon.svg'
import { useTrackEvent } from '../../../../hooks/useTrackEvent/useTrackEvent';
import { useAnalytics } from '../../../app/data/AnalyticsProvider';
import { DeepAnalysisWhatsNext } from './DeepAnalysisWhatsNext';
import { DeepAnalysisReviewBulkEdit } from './DeepAnalysisReviewBulkEdit';
import { ReviewHeaderDropdown, ReviewHeaderDropdownTitle } from './DeepAnalysisReviewResultModal.styles';
import { FilesDropdown } from './FilesDropdown';
import { NotificationTypes } from '../../../notifications/data/notifications.store';
import { AssistanceMessageAvatar } from '../../../assistance/components/Assistance.styles';
import { ChatAvatarIcon } from '../../../assistance/assets/icons';
import { useTypingEffect } from '../../../assistance/hooks/useTypingEffect';
import { calcCurrencies, calcTotals, getFileType, processedLLMResult } from '../../utils/Utils';
import { useVaultsFetchByStatus } from '../../hooks/useVaultsFetchByStatus';
import { addItems, confirmVaults, getVaultsResults } from '../../data/vault.service';
import { VaultDeleteModal } from '../VaultDeleteModal/VaultDeleteModal';
import { ReviewItemsHeaderRow } from './ReviewItemsHeaderRow.js';
import { errorNotification } from '../../../layout/components/Notifications/notifications.js';

const stopFetching = (vaults) => vaults?.every(vault => (('failed' === vault.llmStatus) || ('timeout' === vault.llmStatus) || ('rejected' === vault.llmStatus) || (('done' === vault.llmStatus) && (!!vault.llmResultMetadata?.llmResultURL))));

export const DeepAnalysisReviewResultModal = observer(({ onFlowStepChange }) => {
  const { uiStore, metadataStore, userStore, notificationsStore, vaultStore } = useStore();
  const contentRef = useRef(null);
  const [editedRowIndex, setEditedRowIndex] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useAnalytics();
  const [isResultsReceived, setIsResultsReceived] = useState(false);

  const [isTyping, setIsTyping] = useState(false);
  const [firstItemWithoutAllFields, setFirstItemWithoutAllFields] = useState(null);
  const [showMissingFieldsError, setShowMissingFieldsError] = useState(false);

  const assetClasses = metadataStore.assetClassWithCategoriesForVaultUpload;
  const liabilityClasses = metadataStore.liabilityClassesWithCategoriesForVaultUpload;
  const relevantClasses = [...assetClasses, ...liabilityClasses];

  const { vaults, isReady } = useVaultsFetchByStatus({
    status: 'Pre-Process',
    stopFetching
  });

  const [vaultData, setVaultData] = useState({
    currencyTable: {},
    llmResult: [],
    totals: { assets: { count: 0, sum: 0 }, liabilities: { count: 0, sum: 0 } },
    vaultsResults: []
  });
  const [backupData, setBackupData] = useState(null);
  const { currencyTable, llmResult, totals, vaultsResults, hasResults } = vaultData;

  useEffect(() => {
    const fetchVaultsResults = async () => {
      const llmResults = await getVaultsResults(vaults.map(vault => vault.id));
      const updatedCurrencyTable = await calcCurrencies({}, llmResults?.data?.items);
      const processedVaultsResults = processedLLMResult({
        llmResult: llmResults?.data?.items,
        metadataStore,
        currencyTable: updatedCurrencyTable
      });

      const totalsCalc = calcTotals({ result: processedVaultsResults, metadataStore });

      setVaultData({
        hasResults: true,
        currencyTable: updatedCurrencyTable,
        llmResult: llmResults?.data?.items,
        totals: totalsCalc,
        vaultsResults: processedVaultsResults
      });
    };

    const onAllFailed = async () => {
      await confirmVaults(vaults.map(vault => vault.id));
      vaultStore.setFilesData({ numOfItems: vaults.length, singleFileName: vaults[0].originalName });
      vaultStore.setVaultFormStep('deep-analysis');
    }

    if (isReady) {
      const isAllFailed = vaults.every(vault => (('failed' === vault.llmStatus) || ('timeout' === vault.llmStatus) || ('rejected' === vault.llmStatus)));
      if (isAllFailed) {
        onAllFailed();
      } else {
        fetchVaultsResults();
      }
    }
  }, [isReady, metadataStore, vaults, vaultStore]);

  const updateItemParam = async (index, changes) => {
    if (!llmResult) return;
    const updatedLlmResult = [...llmResult];

    const item = updatedLlmResult[index];
    Object.entries(changes).forEach(([key, value]) => item[key] = value);
    updatedLlmResult.splice(index, 1, item);
    onUpdateLlmResult(updatedLlmResult);
  };


  useTrackEvent('Popup Viewed', {
    title: 'Magic Box AI deep analysis results',
    url: window.location.href
  });

  useEffect(() => {
    if (hasResults && !isResultsReceived) {
      setIsResultsReceived(true);
      trackEvent('Magic box ai results received', {
        url: window.location.href
      });
    }
  }, [hasResults, isResultsReceived, trackEvent]);

  useEffect(() => {
    const firstErrorIndex = vaultData.vaultsResults?.findIndex(item => !item.categoryId || isNaN(item.amount) || !item.currency || !item.item_name);
    setFirstItemWithoutAllFields((-1 === firstErrorIndex) ? null : firstErrorIndex);
  }, [vaultData]);

  const handleCategoryChange = (index, categoryId) => {
    console.log('handleCategoryChange', index, categoryId);
    updateItemParam(index, { categoryId })
  };

  const onScroll = () => {
    if (contentRef.current) {
      const { scrollTop } = contentRef.current
      setIsSticky(scrollTop > 8);
    }
  };

  const onUpdateLlmResult = async (updatedLlmResults) => {
    const updatedCurrencyTable = await calcCurrencies(currencyTable, updatedLlmResults);
    const processedVaultsResults = processedLLMResult({ llmResult: updatedLlmResults, metadataStore, currencyTable: updatedCurrencyTable });
    const totalsCalc = calcTotals({ result: processedVaultsResults, metadataStore });

    setVaultData(prev => ({
      ...prev,
      currencyTable: updatedCurrencyTable,
      llmResult: updatedLlmResults,
      totals: totalsCalc,
      vaultsResults: processedVaultsResults
    }));
  }

  const onDeleteItem = (index) => {
    const updatedLlmResult = [...llmResult];
    updatedLlmResult.splice(index, 1);
    onUpdateLlmResult(updatedLlmResult);
  };

  const onAddItems = async () => {
    setIsLoading(true);
    if (firstItemWithoutAllFields) {
      setShowMissingFieldsError(true);
      const isCategoryMissing = !vaultsResults[firstItemWithoutAllFields].categoryId;
      errorNotification(isCategoryMissing ? "Please select category for all items" : "Please fill all empty fields");
    } else {
      const items = {
        assets: []
      };

      if (vaultsResults?.length) {
        items.assets.push(...vaultsResults.map(item => {
          return {
            categoryId: item.categoryId,
            value: +item.amount,
            currency: item.currency,
            title: item.item_name,
            fund_name: item.fund_name,
            vaultId: item.vaultId
          };
        }))
      }

      try {
        const response = await addItems(items);
        if (response?.statusCode) {
          errorNotification('Something went wrong');
        } else {
          await confirmVaults(vaults.map(vault => vault.id));
          await notificationsStore.removeNotificationByType(NotificationTypes.MAGIC_BOX_AI_DATA_READINESS);
          vaultStore.setFilesData({ numOfItems: llmResult?.length });
          vaultStore.setVaultFormStep('Done');
          await metadataStore.fetchData();
        }
      } catch (e) {
        errorNotification('Something went wrong');
      }
    }

    setIsLoading(false);
  };

  const onClick = async () => {
    if (isTyping || isLoading) return;

    if (hasResults) {
      onAddItems();
      trackEvent('CTA Clicked', {
        cta_text: 'Add Items',
        location: isWelcome ? 'Magic box ai activation flow' : 'Magic box ai page',
        type: "Button",
        url: window.location.href
      });

      trackEvent('Magic box ai items added', { url: window.location.href })
    } else {
      trackEvent('CTA Clicked', {
        cta_text: isWelcome ? 'Go to sync bank' : 'Continue to explore',
        location: isWelcome ? 'Magic box ai activation flow' : 'Magic box ai page',
        // destination: 'manual magic box',
        type: "Button",
        url: window.location.href
      })
      if (isWelcome) {
        onFlowStepChange(1);
      } else {
        window.location.href = 'wealth';
      }
    }
  }

  const isWelcome = userStore.isWelcome;
  const onCloseModal = () => { setShowDeleteModal(true) };

  const deleteFilesInReview = async () => {
    await vaultStore.deleteVaults(vaults.map(vault => vault.id));
    await vaultStore.fetchVaultsFormStep();
  };

  const files = vaults;

  const btnText = getBtnText("items", hasResults, false);

  const toggleAllItemsForBulkEdit = () => {
    const updatedLlmResult = [...llmResult];
    updatedLlmResult.forEach(item => item.isItemForBulkEdit = !isAllItemsSelectedForBulkEdit);
    onUpdateLlmResult(updatedLlmResult);
  }

  const toggleItemForBulkEdit = (index) => {
    const item = vaultsResults[index];
    updateItemParam(index, { isItemForBulkEdit: !item.isItemForBulkEdit })
  }

  const isAllItemsSelectedForBulkEdit = vaultsResults?.every(vault => vault.isItemForBulkEdit);
  const isInBulkEditMode = vaultsResults?.some(vault => vault.isItemForBulkEdit);;

  const addNewItem = (item) => {
    const updatedLlmResult = [...llmResult];
    updatedLlmResult.push(item);
    onUpdateLlmResult(updatedLlmResult);
  }

  return (
    <BaseModal
      borderRadius={8}
      width={'960px'}
      height={'auto'}
      customModalWrapperStyles={{
        maxWidth: '960px',
        position: 'relative'
      }}
      noSidebarMargin={isWelcome}
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <DeepAnalysisReviewResultHeader>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <DeepAnalysisReviewResultHeaderTitle>
              <div>Review new items</div>
            </DeepAnalysisReviewResultHeaderTitle>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <DeepAnalysisWhatsNext />
            <ModalCloseIcon style={{ cursor: 'pointer' }} onClick={onCloseModal} />
          </div>
        </DeepAnalysisReviewResultHeader>


        <ReviewHeader files={files} hasItemsResults={hasResults} doneReviewedFiles={vaults?.length} setIsTyping={setIsTyping} />
      </div >
      <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'scroll' }} onScroll={onScroll} ref={contentRef} >
        {
          !hasResults &&
          <SkeletonRows count={8} />
        }

        {
          hasResults &&
          <>
            <ReviewItemsHeaderRow
              totals={totals}
              isSticky={isSticky}
              hasResult={hasResults}
              toggleAllItemsForBulkEdit={() => toggleAllItemsForBulkEdit("items")}
              isAllItemsSelectedForBulkEdit={isAllItemsSelectedForBulkEdit}
              isInBulkEditMode={isInBulkEditMode}
              totalRows={vaultsResults?.length}
            />

            <DeepAnalysisReviewResultContent>
              <DeepAnalysisReviewResultsWrapper>
                <>
                  {
                    vaultsResults?.map((item, itemIndex) => (
                      <DeepAnalysisResultsRow
                        key={itemIndex}
                        item={item}
                        itemIndex={itemIndex}
                        relevantClasses={relevantClasses}
                        scrollToMe={(((firstItemWithoutAllFields === itemIndex)))}
                        handleCategoryChange={(_classId, categoryId) => handleCategoryChange(itemIndex, categoryId)}
                        isEdited={editedRowIndex === itemIndex}
                        onEditedIndexChange={(index) => setEditedRowIndex(index)}
                        onClickDelete={() => onDeleteItem(itemIndex)}
                        isInBulkEditMode={isInBulkEditMode}
                        isItemForBulkEdit={item.isItemForBulkEdit}
                        onBulkEditChange={() => toggleItemForBulkEdit(itemIndex, "items")}
                        handleFieldChange={(changes) => updateItemParam(itemIndex, changes)}
                        showMissingFieldsError={showMissingFieldsError && (firstItemWithoutAllFields === itemIndex)}
                      />
                    ))
                  }
                  {
                    <DeepAnalysisResultsRow
                      isNew
                      itemIndex={-1}
                      relevantClasses={relevantClasses}
                      isEdited={editedRowIndex === -1}
                      onEditedIndexChange={(index) => setEditedRowIndex(index)}
                      addNewItem={addNewItem}
                      disabled={isInBulkEditMode}
                    />
                  }
                </>
              </DeepAnalysisReviewResultsWrapper>
            </DeepAnalysisReviewResultContent>
          </>
        }
      </div>

      <DeepAnalysisReviewBulkEdit
        isInBulkEditMode={isInBulkEditMode}
        relevantClasses={relevantClasses}
        bulkEditVaults={vaultsResults?.filter(vault => vault.isItemForBulkEdit)}
        exitBulkEditMode={() => {
          const updatedLlmResult = [...llmResult];
          updatedLlmResult.forEach(item => delete item.isItemForBulkEdit);
          onUpdateLlmResult(updatedLlmResult);
        }}
        deleteBulkEditItems={() => {
          const currentLlmResult = [...llmResult];
          const updatedLlmResult = currentLlmResult.filter(item => !item.isItemForBulkEdit);
          onUpdateLlmResult(updatedLlmResult);
        }}
        onSaveBulkEditItems={(fieldsToChange, editField, count) => {
          setBackupData({
            data: llmResult.slice(),
            text: `Successfully updated the ${editField} for ${count} item${(count > 1) ? 's' : ''}`,
            timeoutId: setTimeout(() => setBackupData(null), 5000)
          });

          const currentLlmResult = [...llmResult];
          const updatedLlmResult = currentLlmResult.map(item => {
            if (!item.isItemForBulkEdit) return item;
            return { ...item, ...fieldsToChange, isItemForBulkEdit: false };
          });
          onUpdateLlmResult(updatedLlmResult);
        }}
        backupData={backupData}
        onUndo={() => {
          onUpdateLlmResult(backupData.data);
          clearTimeout(backupData.timeoutId);
          setBackupData(null);
        }}
        clearBackupData={() => setBackupData(null)}
      />

      <DeepAnalysisReviewResultFooter>
        <FooterTotals totals={vaultData.totals} isIncognitoMode={uiStore.isIncognitoMode} visible={!isInBulkEditMode} />
        <PrimaryButton onClick={onClick} disabled={isTyping || (hasResults && !llmResult?.length)} visible={!isInBulkEditMode} style={{ borderRadius: '8px', minWidth: '190px' }}>{(isLoading) ? <LoadingOutlined /> : btnText}</PrimaryButton>
      </DeepAnalysisReviewResultFooter>

      {showDeleteModal && <VaultDeleteModal onClose={() => setShowDeleteModal(false)} onDelete={deleteFilesInReview} />}

    </BaseModal >
  )
});

const ReviewHeader = observer(({ files, hasItemsResults, hasTickersResults, doneReviewedFiles, setIsTyping }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isLoading = (!hasItemsResults && !hasTickersResults);
  const setIsTypingFunc = useCallback(setIsTyping, [setIsTyping]);
  const displayText = useTypingEffect(`The process may take up to 2 minutes.  \nYou can proceed to the next step, and we'll notify you when it's done.`, isLoading, setIsTypingFunc);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const filesCount = files?.length || 0;
  const isSingleFile = (filesCount === 1);
  const title = isSingleFile ? files[0]?.originalName : `New assets found from ${doneReviewedFiles} files`;
  const icon = isSingleFile ? getFileType(files[0]?.originalName) : <VaultFileIcon />;

  const onDropdownClick = (e) => {
    e.stopPropagation();
    if (isSingleFile) {
      return;
    }

    setIsOpen((isOpen) => !isOpen);
  }

  return (
    <ReviewHeaderWrapper>
      <ReviewHeaderDropdownWrapper>
        <ReviewHeaderDropdown onMouseDown={onDropdownClick} isClickable={!isSingleFile}>
          <ReviewHeaderDropdownTitle>
            {icon}
            {
              (isLoading && !isSingleFile) ?
                <LoadingOutlined style={{ fontSize: '24px' }} /> :
                <div>{title}</div>
            }
          </ReviewHeaderDropdownTitle>
          {
            (!isSingleFile) &&
            <ReviewHeaderDropdownIconWrapper>
              <div>{filesCount} files</div>
              <DropdownIconWrapper isOpen={isOpen}>
                <VaultChevronDownIcon />
              </DropdownIconWrapper>
            </ReviewHeaderDropdownIconWrapper>
          }
        </ReviewHeaderDropdown>
        {isOpen && <FilesDropdown files={files} />}
      </ReviewHeaderDropdownWrapper>

      {
        (isLoading) &&
        <>
          <ReviewHeaderMessageWrapper>
            <AssistanceMessageAvatar>
              <ChatAvatarIcon />
            </AssistanceMessageAvatar>
            <ReviewHeaderMessageText>
              {displayText}
            </ReviewHeaderMessageText>
          </ReviewHeaderMessageWrapper>


          <ReviewHeaderMessageWrapper>
            <AssistanceMessageAvatar>
              <ChatAvatarIcon />
            </AssistanceMessageAvatar>
            <VaultSingleFileDescriptionEmpty>
              {`Analyzing your file${(isSingleFile) ? '' : 's'} structure…`}
            </VaultSingleFileDescriptionEmpty>
          </ReviewHeaderMessageWrapper>
        </>
      }
    </ReviewHeaderWrapper>
  )
})

const SkeletonRows = ({ count }) => {
  return (
    Array.from({ length: count }, (_, index) => (
      <DeepAnalysisResultsRow key={`skeleton-${index}`} showSkeleton={true} />
    ))
  );
};

const FooterTotals = ({ totals, isIncognitoMode, visible }) => {

  return (
    <FooterTotalsWrapper visible={visible}>
      <FooterTotalsItem>
        <FooterTotalItemsValue>{displayMoneyValue(totals?.assets.sum, 'USD', isIncognitoMode, 100)}</FooterTotalItemsValue>
        <FooterTotalItemsLabel>Total Assets ({totals?.assets.count})</FooterTotalItemsLabel>
      </FooterTotalsItem>
      <FooterTotalsItem>
        <FooterTotalItemsValue>{displayMoneyValue(totals?.liabilities.sum, 'USD', isIncognitoMode, 100)}</FooterTotalItemsValue>
        <FooterTotalItemsLabel>Total Liabilities ({totals?.liabilities.count})</FooterTotalItemsLabel>
      </FooterTotalsItem>
    </FooterTotalsWrapper>
  );
};

const getBtnText = (step, hasItemsResults, hasTickersResults) => {
  if (!step || (!hasItemsResults && !hasTickersResults)) {
    return "Notify me when it's done";
  }
  return ((("tickers" === step) || (("items" === step) && !hasTickersResults)) ? "Continue to deep analysis" : "Continue to review tickers");
}